// /buy without ?order= — choose the tier, then hand off to the QRIS checkout
// (pages/buy/Checkout). Free-first: the page states what a keyless install already
// does, then what a Pro key takes off, and only then the price and the action —
// the price is never the first thing on screen, and there is no side-by-side tier
// grid for Pro to dominate.
//
// POST /orders opens the charge; the new order id then goes into the URL
// (?order=<id>), so checkout survives a refresh and /purchases can deep-link back
// into an unpaid order. Payment capture is NEVER in-app — we only open the charge
// and render the QRIS the provider froze onto the order.
//
// Everything that could make the action wrong is read BEFORE it renders:
//  - an active paid license → there is nothing to buy (POST /orders answers 409).
//  - a still-payable pending order for the tier → resume it, never mint a second
//    (the server also caps pending orders per user at 5).
//  - GET /pricing listing no paid tier → new sales are switched off in this
//    environment (SALES_ENABLED=false, the API default everywhere; docs/API.md §3).
//    The page says so plainly instead of offering a button that would 410.
// Card anatomy: shadcn-ui/ui apps/v4/registry/new-york-v4/ui/card.tsx — MIT.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Glyph } from "@/ui";
import { dataClass } from "@ahcut/ui/recipes";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Field,
} from "@/ui";
import { PageHeader } from "@/components/PageHeader";
import { KeylessExportLimits } from "@/components/KeylessExportLimits";
import { Loading, ErrorState } from "@/components/States";
import { cn } from "@/lib/cn";
import { useAsync } from "@/lib/useAsync";
import { getPricing, paidTiers } from "@/lib/pricing";
import { KEYLESS_EXPORT_LIMITS } from "@/lib/limits";
import { listLicenses } from "@/lib/license";
import {
  COUPON_SESSION_KEY,
  createOrder,
  listOrders,
  newIdempotencyKey,
  orderPastExpiry,
  previewCoupon,
} from "@/lib/orders";
import { formatDate, formatIDR, tierLabel } from "@/lib/format";
import { ApiError } from "@/lib/api";
import { toError } from "@/lib/errors";
import { MARKETING_BASE } from "@/lib/config";
import type { CouponPreview, License, Order, PaidTierId, Pricing, PricingTier } from "@/lib/types";

const PAGE_TITLE = "Beli Pro";

/** 410 from POST /orders or GET /coupons/:code — new sales are switched off.
 *  Permanent for the client (docs/API.md §0): state it, never retry. */
function isSalesClosed(err: unknown): boolean {
  return err instanceof ApiError && err.code === "sales_closed";
}

/**
 * What a Pro key takes off, read from the desktop app at v2.0.0 (the licence gate
 * plus the export panel's own resolution picker). Each row names the free limit it
 * removes, so it reads against the keyless list above it.
 *
 * Deliberately NOT claimed here, because the app does not do it: no 8K (a sanity
 * ceiling rejects any dimension above 4320) and no "durasi tanpa batas" full stop
 * (a 10-hour ceiling remains) — hence "batas … dilepas" rather than "tanpa batas".
 * Pro also unlocks no modules and no AI quota; neither is mentioned.
 *
 * The two numbers are NOT typed here: both rows name the free limit they remove by
 * reading it from @/lib/limits, the same constant the keyless card above renders
 * from, so the two cards can never disagree (ui/DESIGN.md §9, D40 (2)).
 */
const { maxResolution, maxMinutes } = KEYLESS_EXPORT_LIMITS;

const PRO_UNLOCKS: readonly { term: string; value: string }[] = [
  { term: "Resolusi maksimal", value: `Batas ${maxResolution} dilepas — 2K dan 4K bisa dipilih` },
  { term: "Durasi maksimal", value: `Batas ${maxMinutes} menit dilepas — sampai 10 jam` },
  { term: "Akhir video", value: "Tanpa kartu penutup" },
  { term: "Preset Pro", value: "Tiga preset kurasi di kategori Circle: Ribbon, Blob, Emblem" },
];

export function TierChoice() {
  const [params, setParams] = useSearchParams();
  const preselect = params.get("tier");

  const pricing = useAsync<Pricing>((s) => getPricing(s), []);
  // Licenses and pending orders both gate the action. This screen unmounts while a
  // checkout is open (Buy.tsx swaps in CheckoutRoute) and mounts fresh when it
  // closes, so returning here after paying re-reads both instead of showing a stale
  // "Beli Pro" to someone who now owns it.
  const licenses = useAsync<License[]>((s) => listLicenses(s), []);
  const orders = useAsync<Order[]>((s) => listOrders(s), []);

  // A 410 seen live (sales switched off between the /pricing read and the click,
  // or a /pricing response cached from before the switch). Same destination as an
  // empty paid catalog: the plain not-on-sale state, never a red error.
  const [salesClosedLive, setSalesClosedLive] = useState(false);

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<ApiError | Error | null>(null);

  // One key per checkout INTENT (tier + coupon), kept until the order actually
  // exists — a retry after a network timeout must return the same order, not a
  // second charge. A coupon swap is a different intent, so it is part of the key.
  const idemKeys = useRef<Record<string, string>>({});

  // ---- Coupon ----
  // previewCoupon() creates nothing, so it is safe to run speculatively (the
  // auto-apply from a deep link below). buy() sends the previewed code along and
  // the server re-validates it regardless.
  const [couponCode, setCouponCode] = useState("");
  const [couponPreview, setCouponPreview] = useState<CouponPreview | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState<ApiError | Error | null>(null);
  const [promoDismissed, setPromoDismissed] = useState(false);

  const applyCoupon = useCallback(async (code: string, tier: PaidTierId) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setCouponLoading(true);
    setCouponError(null);
    try {
      setCouponPreview(await previewCoupon(trimmed, tier));
    } catch (err) {
      setCouponPreview(null);
      if (isSalesClosed(err)) setSalesClosedLive(true);
      else setCouponError(toError(err));
    } finally {
      setCouponLoading(false);
    }
  }, []);

  const removeCoupon = useCallback(() => {
    setCouponCode("");
    setCouponPreview(null);
    setCouponError(null);
    // An explicit refusal: don't let the promo hint nag again this page session.
    setPromoDismissed(true);
  }, []);

  // The active paid license, if any — with one there is nothing left to buy.
  const ownedPaid = useMemo(
    () => (licenses.data ?? []).find((l) => l.tier !== "free" && !l.revoked_at) ?? null,
    [licenses.data],
  );

  // Newest still-payable pending order per tier (GET /orders is newest-first) — the
  // resumable one. A pending order past its charge deadline is dead: it must not be
  // offered as "continue", because that QRIS can no longer be paid.
  const pendingByTier = useMemo(() => {
    const m = new Map<string, Order>();
    for (const o of orders.data ?? []) {
      if (o.status === "pending" && !orderPastExpiry(o) && !m.has(o.tier)) m.set(o.tier, o);
    }
    return m;
  }, [orders.data]);

  // The tier this page acts on: ?tier= when it names a paid tier the catalog
  // actually lists (the desktop app opens /buy?tier=pro), else the first paid tier.
  const sellable = useMemo(() => paidTiers(pricing.data), [pricing.data]);
  const tier: PricingTier | null =
    sellable.find((t) => t.id === preselect) ?? sellable[0] ?? null;

  const pending = tier ? (pendingByTier.get(tier.id) ?? null) : null;

  // A resumable pending order already froze its own amount — a fresh coupon preview
  // cannot re-price it, so offering one next to it would mislead. Retire any preview
  // the moment such an order surfaces.
  useEffect(() => {
    if (pending) {
      setCouponPreview(null);
      setCouponError(null);
    }
  }, [pending]);

  // Auto-apply a coupon deep link: ?coupon=CODE on this URL, or the one auth.tsx
  // stashed at boot when the link arrived before login. Runs once per mount — the
  // ref guard keeps StrictMode's double-invoke from firing two previews (and from
  // double-consuming the sessionStorage key). Waits for a tier: the preview is
  // priced against one.
  const couponAutoApplied = useRef(false);
  useEffect(() => {
    if (couponAutoApplied.current || !tier) return;
    couponAutoApplied.current = true;
    const fromUrl = params.get("coupon");
    const fromSession = sessionStorage.getItem(COUPON_SESSION_KEY);
    sessionStorage.removeItem(COUPON_SESSION_KEY);
    const code = fromUrl || fromSession;
    if (code) {
      setCouponCode(code.toUpperCase());
      void applyCoupon(code, tier.id as PaidTierId);
    }
    // Deliberately once-per-mount: re-running on every param change would fight the
    // user's own Hapus / re-Terapkan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tier]);

  const openOrder = useCallback(
    (id: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("order", id);
          return next;
        },
        { replace: false },
      );
    },
    [setParams],
  );

  const buy = useCallback(
    async (target: PaidTierId, coupon?: string) => {
      setCreating(true);
      setCreateError(null);
      try {
        const mapKey = `${target}:${coupon || "none"}`;
        const key = (idemKeys.current[mapKey] ??= newIdempotencyKey(target, coupon));
        const res = await createOrder(target, key, coupon);
        delete idemKeys.current[mapKey]; // intent fulfilled — the next purchase is a new one
        openOrder(res.order_id);
      } catch (err) {
        const asError = toError(err);
        if (isSalesClosed(asError)) {
          setSalesClosedLive(true);
        } else if (coupon && asError instanceof ApiError && asError.code === "coupon_invalid") {
          // Valid at preview time but refused at create (e.g. the quota was consumed
          // in between). The "applied" state is stale and must stop offering a coupon
          // the server just rejected; the message belongs where previews fail, not
          // ALSO in a page-level banner. The typed code stays so it can be corrected.
          setCouponPreview(null);
          setCouponError(asError);
        } else {
          setCreateError(asError);
        }
      } finally {
        setCreating(false);
      }
    },
    [openOrder],
  );

  // The h1 renders in every state (ui/DESIGN.md §10).
  const header = (
    <PageHeader
      title={PAGE_TITLE}
      lede="ahcut gratis dan tetap gratis. Pro melepas batas ekspornya — bayar sekali, berlaku untuk semua pembaruan berikutnya."
    />
  );

  if (pricing.loading) {
    return (
      <>
        {header}
        <Loading label="Memuat harga…" />
      </>
    );
  }
  if (pricing.error) {
    return (
      <>
        {header}
        <ErrorState error={pricing.error} onRetry={pricing.reload} />
      </>
    );
  }

  // Ownership must be known before an action renders: a failed /license read would
  // otherwise offer a purchase to someone who already owns Pro (the server refuses
  // it with 409, but the screen must not ask for money it knows is not due).
  if (licenses.loading) {
    return (
      <>
        {header}
        <Loading label="Memuat status lisensi…" />
      </>
    );
  }
  if (licenses.error) {
    return (
      <>
        {header}
        <ErrorState error={licenses.error} onRetry={licenses.reload} />
      </>
    );
  }

  // Pending orders gate it the same way: without them a click would mint a duplicate
  // order for a charge that is already open and still payable.
  if (orders.loading) {
    return (
      <>
        {header}
        <Loading label="Memuat pesanan…" />
      </>
    );
  }
  if (orders.error) {
    return (
      <>
        {header}
        <ErrorState error={orders.error} onRetry={orders.reload} />
      </>
    );
  }

  const closed = salesClosedLive || tier === null;
  const promo = pricing.data?.promo ?? null;
  const promoTier = tier && promo ? promo.tiers?.[tier.id] : undefined;
  const showPromoHint =
    !!promo && !!promoTier && !promoDismissed && !couponPreview && !pending && !ownedPaid && !closed;

  // Fragment: the shell's content column (Layout app-main-inner) sets the rhythm.
  return (
    <>
      {header}

      {/* Free first: what the reader already has, stated as fact, before any offer. */}
      <Card>
        <CardHeader>
          <CardTitle>Yang kamu punya sekarang</CardTitle>
          <CardDescription>
            Tanpa kunci lisensi, video yang kamu ekspor mengikuti batas berikut.
          </CardDescription>
        </CardHeader>
        <KeylessExportLimits />
      </Card>

      {/* Only where Pro can actually be bought: on a server with sales switched off
          this would advertise an unavailable product and then say so underneath. */}
      {!closed && (
        <Card>
          <CardHeader>
            <CardTitle>Yang dibuka Pro</CardTitle>
            <CardDescription>
              Satu kunci, melepas batas di atas. Selain itu aplikasinya sama.
            </CardDescription>
          </CardHeader>
          <DescriptionList>
            {PRO_UNLOCKS.map((row) => (
              <div key={row.term} className="contents">
                <DescriptionTerm>{row.term}</DescriptionTerm>
                <DescriptionDetails>{row.value}</DescriptionDetails>
              </div>
            ))}
          </DescriptionList>
        </Card>
      )}

      {ownedPaid ? (
        <OwnedCard license={ownedPaid} />
      ) : closed ? (
        <SalesClosedCard />
      ) : (
        tier && (
          <>
            {showPromoHint && promo && promoTier && (
              <Alert title="Promo aktif">
                <div className="flex flex-col items-start gap-3">
                  <p>
                    Kupon <strong>{promo.code}</strong> memotong{" "}
                    <span className={dataClass}>{formatIDR(promoTier.discount)}</span>, jadi{" "}
                    <span className={dataClass}>{formatIDR(promoTier.final)}</span>.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      variant="quiet"
                      disabled={couponLoading || creating}
                      onClick={() => {
                        setCouponCode(promo.code);
                        void applyCoupon(promo.code, tier.id as PaidTierId);
                      }}
                    >
                      Terapkan {promo.code}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setPromoDismissed(true)}>
                      Sembunyikan
                    </Button>
                  </div>
                </div>
              </Alert>
            )}

            <PurchaseCard
              tier={tier}
              preview={couponPreview}
              pending={pending}
              creating={creating}
              createError={createError}
              onBuy={() => void buy(tier.id as PaidTierId, couponPreview?.code)}
              onResume={() => pending && openOrder(pending.id)}
            />

            {!pending && (
              <CouponBox
                code={couponCode}
                onCodeChange={setCouponCode}
                onApply={() => void applyCoupon(couponCode, tier.id as PaidTierId)}
                onRemove={removeCoupon}
                loading={couponLoading}
                preview={couponPreview}
                error={couponError}
                disabled={creating}
              />
            )}
          </>
        )
      )}

      <Card tone="subtle">
        <CardHeader>
          <CardTitle as="h3">Ketentuan lisensi</CardTitle>
        </CardHeader>
        <ul className="flex list-disc flex-col gap-2 pl-5 text-sm text-fg-1 marker:text-fg-2">
          <li>
            Bayar sekali, berlaku selamanya. Semua pembaruan termasuk versi mayor sudah tercakup —
            tidak ada langganan dan tidak ada auto-debit.
          </li>
          <li>
            Kunci terikat ke satu perangkat. Pindah ke perangkat lain bisa sekali per 30 hari, dan
            perangkat lama berhenti berlaku setelah dipindahkan.
          </li>
          <li>Refund atau chargeback mencabut lisensi.</li>
          <li>
            Pembayaran lewat QRIS. Yang ditagih adalah harga ditambah fee QRIS — angka pastinya
            tampil di halaman pembayaran sebelum kamu bayar.
          </li>
        </ul>
        <p className="text-sm text-fg-2">
          Selengkapnya di{" "}
          <a href={`${MARKETING_BASE}/syarat`} target="_blank" rel="noopener noreferrer">
            Syarat
          </a>{" "}
          dan{" "}
          <a href={`${MARKETING_BASE}/refund`} target="_blank" rel="noopener noreferrer">
            Kebijakan Refund
          </a>
          .
        </p>
      </Card>
    </>
  );
}

/* The price and the one action. A pending order for this tier replaces the purchase
   with a resume: its amount was frozen when it was created, so it is the number shown. */
function PurchaseCard({
  tier,
  preview,
  pending,
  creating,
  createError,
  onBuy,
  onResume,
}: {
  tier: PricingTier;
  preview: CouponPreview | null;
  pending: Order | null;
  creating: boolean;
  createError: ApiError | Error | null;
  onBuy: () => void;
  onResume: () => void;
}) {
  // list_amount is the server's frozen pre-discount price for THIS preview — using
  // the catalog price instead could drift from what final_amount was computed against.
  const list = pending ? (pending.list_amount ?? pending.amount) : (preview?.list_amount ?? tier.price);
  const payable = pending ? pending.amount : (preview?.final_amount ?? tier.price);
  const discounted = payable < list;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{tierLabel(tier.id)}</CardTitle>
      </CardHeader>

      <div className="flex flex-col gap-1">
        <p className={cn(dataClass, "text-price text-fg-0")}>{formatIDR(payable)}</p>
        {discounted && (
          <p className="text-sm text-fg-2">
            Harga awal <s className={dataClass}>{formatIDR(list)}</s>
            {pending?.coupon_code
              ? ` — kupon ${pending.coupon_code} sudah terpakai di pesanan ini.`
              : preview
                ? ` — kupon ${preview.code} diterapkan.`
                : "."}
          </p>
        )}
        <p className="text-sm text-fg-2">Sekali bayar. Belum termasuk fee QRIS.</p>
      </div>

      {pending ? (
        <>
          <p className="text-fg-1">
            Pesanan {formatDate(pending.created_at)} masih menunggu pembayaran. Lanjutkan yang itu —
            membuat pesanan baru hanya menambah tagihan yang tidak terpakai.
          </p>
          <div>
            <Button variant="primary" onClick={onResume}>
              Lanjutkan pembayaran
            </Button>
          </div>
        </>
      ) : (
        <>
          <div>
            <Button variant="primary" disabled={creating} onClick={onBuy}>
              {creating ? "Membuat pesanan…" : `Beli ${tier.name}`}
            </Button>
          </div>
          <p className="text-sm text-fg-2">
            Kamu akan diarahkan ke halaman pembayaran QRIS. Belum ada yang ditagihkan sampai kamu
            membayar.
          </p>
        </>
      )}

      {createError && <ErrorState variant="inline" error={createError} live />}
    </Card>
  );
}

/* Coupon entry: one input while none is applied, the applied code and its discount
   once one is. Sits BELOW the price it modifies. */
function CouponBox({
  code,
  onCodeChange,
  onApply,
  onRemove,
  loading,
  preview,
  error,
  disabled,
}: {
  code: string;
  onCodeChange: (v: string) => void;
  onApply: () => void;
  onRemove: () => void;
  loading: boolean;
  preview: CouponPreview | null;
  error: ApiError | Error | null;
  disabled: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle as="h3">Punya kode kupon?</CardTitle>
      </CardHeader>
      {preview ? (
        <>
          <p className="text-fg-1">
            <strong>{preview.code}</strong> memotong{" "}
            <span className={dataClass}>{formatIDR(preview.discount)}</span>, jadi{" "}
            <span className={dataClass}>{formatIDR(preview.final_amount)}</span>.
          </p>
          <div>
            <Button variant="ghost" size="sm" onClick={onRemove} disabled={disabled}>
              Hapus kupon
            </Button>
          </div>
        </>
      ) : (
        <form
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            onApply();
          }}
        >
          <Field
            className="flex-1"
            label="Kode kupon"
            value={code}
            onChange={(e) => onCodeChange(e.target.value.toUpperCase())}
            maxLength={32}
            spellCheck={false}
            disabled={loading || disabled}
          />
          <Button
            type="submit"
            variant="secondary"
            disabled={loading || disabled || !code.trim()}
          >
            {loading ? "Memeriksa…" : "Terapkan"}
          </Button>
        </form>
      )}
      {error && <ErrorState variant="inline" error={error} live />}
    </Card>
  );
}

/* Already owns Pro — nothing to sell, so the screen points at the key instead. */
function OwnedCard({ license }: { license: License }) {
  return (
    <Card>
      <div>
        <Badge tone="ok" dot>
          Sudah dimiliki
        </Badge>
      </div>
      <CardHeader>
        <CardTitle>Kamu sudah punya {tierLabel(license.tier)}</CardTitle>
      </CardHeader>
      <p className="text-fg-1">
        Lisensinya berlaku untuk semua pembaruan ahcut, jadi tidak ada yang perlu dibeli lagi.
        Aktivasi ke perangkat atau pindahkan ke perangkat lain di halaman Lisensi.
      </p>
      <div className="flex flex-wrap gap-3">
        <Button asChild variant="primary">
          <Link to="/license">
            <Glyph name="key" />
            Buka Lisensi
          </Link>
        </Button>
        <Button asChild variant="quiet">
          <Link to="/purchases">
            <Glyph name="receipt" />
            Riwayat pembelian
          </Link>
        </Button>
      </div>
    </Card>
  );
}

/* GET /pricing lists no paid tier, or an order/preview came back 410: the API this
   dashboard talks to has new sales switched off (SALES_ENABLED=false — its default,
   so this is what every dev machine shows). State it; never offer a dead button. */
function SalesClosedCard() {
  return (
    <Card>
      <div>
        <Badge tone="neutral" dot>
          Belum bisa dibeli
        </Badge>
      </div>
      <CardHeader>
        <CardTitle>Pro belum dijual di server ini</CardTitle>
      </CardHeader>
      <p className="text-fg-1">
        Server yang dipakai dashboard ini sedang tidak menerima pesanan baru, jadi Pro belum bisa
        dibeli dari sini. Batas ekspor tanpa kunci di atas tetap berlaku dan aplikasinya tetap bisa
        dipakai.
      </p>
      <p className="text-sm text-fg-1">
        Kunci yang sudah dibeli tetap berlaku sepenuhnya — aktivasi dan pemindahan perangkat ada di
        halaman Lisensi, riwayat pesanan di Pembelian.
      </p>
      <div className="flex flex-wrap gap-3">
        <Button asChild variant="quiet">
          <Link to="/license">
            <Glyph name="key" />
            Buka Lisensi
          </Link>
        </Button>
        <Button asChild variant="quiet">
          <Link to="/purchases">
            <Glyph name="receipt" />
            Riwayat pembelian
          </Link>
        </Button>
      </div>
    </Card>
  );
}
